import { useCallback, useEffect, useState } from "react";
import { Crown, Users2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

/** Super admin: how many accounts each administrator may create. */
export const UserCapsCard = () => {
  const [data, setData] = useState({ default_cap: 10, admins: [] });
  const [edit, setEdit] = useState({});

  const load = useCallback(async () => {
    try { const res = await api.get("/admin/user-caps"); setData(res.data); }
    catch (e) { toast.error(errMsg(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (a) => {
    const value = Number(edit[a.user_id]);
    if (!Number.isFinite(value) || value < 0) { toast.error("Enter a number"); return; }
    try {
      await api.put(`/admin/users/${a.user_id}/user-cap`, { max_users: value });
      toast.success(`${a.email} can now create ${value} accounts`);
      setEdit((e) => ({ ...e, [a.user_id]: undefined }));
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="rounded-md border border-[#E5E7EB] bg-white p-5" data-testid="user-caps-card">
      <div className="flex items-start gap-3">
        <Users2 className="h-5 w-5 text-[#002FA7] mt-0.5" />
        <div>
          <h2 className="font-heading font-bold text-[#111827]">Account limits</h2>
          <p className="text-xs text-[#6B7280] mt-0.5">
            How many user accounts each administrator may create. Default {data.default_cap}.
          </p>
        </div>
      </div>

      <div className="mt-4 space-y-2">
        {(data.admins || []).length === 0 && (
          <p className="text-sm text-[#6B7280]" data-testid="user-caps-empty">No administrators yet.</p>
        )}
        {(data.admins || []).map((a) => (
          <div key={a.user_id} className="flex flex-wrap items-center gap-2 border-t border-[#E5E7EB] pt-2"
            data-testid={`cap-row-${a.email}`}>
            <div className="flex-1 min-w-[180px]">
              <p className="text-sm text-[#111827]">{a.name || a.email}
                {a.is_primary_admin && <span className="ml-1.5 text-[10px] uppercase text-[#002FA7]">super</span>}
              </p>
              <p className="text-xs text-[#6B7280]">
                {a.email} · created {a.created_count} of {a.is_primary_admin ? "unlimited" : a.max_users}
              </p>
            </div>
            {!a.is_primary_admin && (
              <>
                <Input type="number" min={0} className="w-24 h-9" data-testid={`cap-input-${a.email}`}
                  value={edit[a.user_id] ?? a.max_users}
                  onChange={(e) => setEdit({ ...edit, [a.user_id]: e.target.value })} />
                <Button size="sm" variant="outline" onClick={() => save(a)} data-testid={`cap-save-${a.email}`}>
                  Save
                </Button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/** Super admin: hand the role over to another administrator. Irreversible for the outgoing admin. */
export const SuperAdminHandoverCard = ({ users = [], onDone }) => {
  const [target, setTarget] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const candidates = users.filter((u) => u.role === "admin" && !u.is_primary_admin && u.status === "active");
  const chosen = candidates.find((u) => u.user_id === target);

  const transfer = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Hand the super administrator role to ${chosen?.email}? `
      + "You will immediately become a regular administrator.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/super-admin/transfer",
        { user_id: target, confirm_email: confirm.trim() });
      toast.success(`${data.super_admin} is now the super administrator`, { duration: 15000 });
      setTarget(""); setConfirm("");
      onDone?.();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border border-[#FCC419] bg-[#FFFBEA] p-5" data-testid="handover-card">
      <div className="flex items-start gap-3">
        <Crown className="h-5 w-5 text-[#B58900] mt-0.5" />
        <div>
          <h2 className="font-heading font-bold text-[#111827]">Hand over super administrator</h2>
          <p className="text-xs text-[#6B7280] mt-0.5">
            The chosen administrator gains every super-admin power and full visibility of the previous
            super admin's audit and login history. You drop to a regular administrator straight away.
            Requires two-factor authentication on their account.
          </p>
        </div>
      </div>

      <form onSubmit={transfer} className="mt-4 grid sm:grid-cols-2 gap-3">
        <select value={target} onChange={(e) => { setTarget(e.target.value); setConfirm(""); }}
          data-testid="handover-select"
          className="h-10 rounded-md border border-[#E5E7EB] bg-white px-2 text-sm">
          <option value="">Choose an administrator…</option>
          {candidates.map((u) => (
            <option key={u.user_id} value={u.user_id}>
              {u.email}{u.totp_enabled ? "" : " (2FA off)"}
            </option>
          ))}
        </select>
        <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="handover-confirm-email"
          placeholder="Type their email to confirm" className="bg-white" />
        <div className="sm:col-span-2">
          <Button type="submit" data-testid="handover-btn"
            disabled={busy || !target || confirm.trim().toLowerCase() !== (chosen?.email || "").toLowerCase()}
            className="w-full bg-[#B58900] hover:bg-[#96700a] text-white">
            Transfer super administrator role
          </Button>
        </div>
      </form>
      {candidates.length === 0 && (
        <p className="text-xs text-[#6B7280] mt-3" data-testid="handover-empty">
          No eligible administrator yet — create an admin and ask them to switch on two-factor authentication.
        </p>
      )}
    </div>
  );
};
